import React from 'react'
import { ApplicationModel } from '@/models/application.model'
import { FileText, Calendar, User, MapPinned } from 'lucide-react'
import { Button } from '@/components/ui/button'

const primary = '#7b2a40'

const getStatusColor = (status?: string) => {
  switch (status?.toLowerCase()) {
    case 'approved':
    case 'issued':
      return 'bg-green-100 text-green-700'
    case 'pending':
    case 'under review':
      return 'bg-yellow-100 text-yellow-700'
    case 'rejected':
      return 'bg-red-100 text-red-700'
    default:
      return 'bg-gray-100 text-gray-700'
  }
}

interface ApplicationCardProps {
  app: ApplicationModel
  onView: (app: ApplicationModel) => void
  programmeName?: string
}

export const ApplicationCard: React.FC<ApplicationCardProps> = ({ app, onView, programmeName }) => {
  const statusClass = getStatusColor(app.status ?? '')
  const paymentPercent = app.paymentPercentComplete ?? 0
  const statusValue = (app.status ?? '').toString().toLowerCase()

  return (
    <div className="bg-white border rounded-xl shadow-sm hover:shadow-md transition overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between p-5 pb-3">
        <div className="flex items-center gap-3">
          <div className="bg-[#f9f5f6] rounded-full p-2">
            <User size={20} strokeWidth={1.5} className="text-[#7b2a40]" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {app.first_name ?? '—'} {app.last_name ?? ''}
            </h3>
            <p className="text-sm text-gray-500">Admission Application</p>
          </div>
        </div>

        <span
          className={`text-xs font-medium px-2.5 py-1 rounded-full ${statusClass}`}
        >
          {app.status ?? '—'}
        </span>
      </div>

      {/* Body */}
      <div className="px-5 pb-4 space-y-3 text-sm text-gray-700">
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-gray-500" />
          <span className="font-medium">{app.app_id ?? '—'}</span>
        </div>

        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-gray-500" />
          <span>
            Submitted{' '}
            {app.submitted_at
              ? new Date(app.submitted_at).toLocaleDateString()
              : '—'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <MapPinned size={16} className="text-gray-500" />
          <span>{app.nationality ?? '—'}</span>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-2">
          <div>
            <p className="text-gray-500 text-xs">Programme</p>
            <p className="font-medium">
              {programmeName ?? app.programme_id ?? '—'}
            </p>
          </div>
          <div>
            <p className="text-gray-500 text-xs">JAMB Score</p>
            <p className="font-medium">{app.jamb_score ?? '—'}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs">Payment Status</p>
            <p className="font-medium capitalize">
              {app.payment_status ?? '—'}
            </p>
          </div>
        </div>

        <div className="pt-2">
          <p className="text-xs text-gray-500">Payment progress</p>
          <div className="mt-1 h-2 w-full rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-[#7b2a40]"
              style={{ width: `${paymentPercent}%` }}
              aria-label={`Payment progress ${paymentPercent}%`}
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">{paymentPercent}% complete</p>
        </div>

        {statusValue === 'pending' && (
          <p className="text-xs text-yellow-700 pt-1">
            Application is currently under review
          </p>
        )}
        {statusValue === 'rejected' && (
          <p className="text-xs text-red-700 pt-1">
            Application was not successful
          </p>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-5 py-3 border-t bg-gray-50">
        <Button className='w-full' onClick={() => onView(app)}>
          View Details
        </Button>
      </div>
    </div>
  )
}
